import React from "react";

import { COLORS, COLORS_PASTEL } from "../../constants";

interface ModelOption {
  background: string | undefined;
  name: string;
  className?: string;
}

export type ModelOptionsType = {
  customer: ModelOption;
  artist: ModelOption;
  release: ModelOption;
  track: ModelOption;
  item: ModelOption;
  transaction: ModelOption;
  csvImport: ModelOption;
  period: ModelOption;
  contract: ModelOption;
  subContract: ModelOption;
  split: ModelOption;
  payout: ModelOption;
  payee: ModelOption;
  ledger: ModelOption;
  payeeSummary: ModelOption;
  statement: ModelOption;
  statementGroup: ModelOption;
  reports: ModelOption;
  mechanicals: ModelOption;
  ppd: ModelOption;
  reserves: ModelOption;
  territory: ModelOption;
  formatGroup: ModelOption;
};

export const MODEL_OPTIONS: ModelOptionsType = {
  customer: {
    background: COLORS_PASTEL[11],
    name: "Customer",
    className: "text-dark",
  },
  artist: {
    background: COLORS_PASTEL[3],
    name: "Artist",
    className: "text-dark",
  },
  release: {
    background: COLORS_PASTEL[2],
    name: "Release",
    className: "text-dark",
  },
  track: {
    background: COLORS[6],
    name: "Track",
    className: "text-dark",
  },
  item: {
    background: COLORS_PASTEL[7],
    name: "Item",
    className: "text-dark",
  },
  transaction: {
    background: COLORS[0],
    name: "Transaction",
    className: "text-dark",
  },
  csvImport: {
    background: COLORS_PASTEL[10],
    name: "Data Import",
    className: "text-dark",
  },
  period: {
    background: COLORS[1],
    name: "Period",
    className: "text-light",
  },
  contract: {
    background: COLORS[2],
    name: "Contract",
    className: "text-dark",
  },
  subContract: {
    background: COLORS_PASTEL[0],
    name: "Sub Contract",
    className: "text-dark",
  },
  split: {
    background: COLORS[2],
    name: "Split",
    className: "text-dark",
  },
  payout: {
    background: COLORS[9],
    name: "Payout",
    className: "text-light",
  },
  payee: {
    background: COLORS[10],
    name: "Payee",
    className: "text-dark",
  },
  ledger: {
    background: COLORS_PASTEL[5],
    name: "Ledger",
    className: "text-dark",
  },
  payeeSummary: {
    background: COLORS_PASTEL[0],
    name: "Payee Summary",
    className: "text-dark",
  },
  statement: {
    background: COLORS[8],
    name: "statement",
    className: "text-dark",
  },
  statementGroup: {
    background: COLORS[8],
    name: "Statement Group",
    className: "text-dark",
  },
  reports: {
    background: COLORS[7],
    name: "reports",
    className: "text-dark",
  },
  mechanicals: {
    background: "#669999",
    name: "Mechanicals",
    className: "text-light",
  },
  ppd: {
    background: "#669999",
    name: "PPD",
    className: "text-light",
  },
  reserves: {
    background: "#669999",
    name: "Reserves",
    className: "text-light",
  },
  territory: {
    background: COLORS_PASTEL[11],
    name: "Territory",
  },
  formatGroup: {
    background: COLORS_PASTEL[2],
    name: "Format Group",
  },
};

type ContentTypeModelsType = {
  artist: keyof ModelOptionsType;
  release: keyof ModelOptionsType;
  track: keyof ModelOptionsType;
  item: keyof ModelOptionsType;
  transaction: keyof ModelOptionsType;
  csvimport: keyof ModelOptionsType;
  period: keyof ModelOptionsType;
  contract: keyof ModelOptionsType;
  payout: keyof ModelOptionsType;
  payee: keyof ModelOptionsType;
  ledger: keyof ModelOptionsType;
  period_payee: keyof ModelOptionsType;
  split: keyof ModelOptionsType;
  statement: keyof ModelOptionsType;
  statement_group: keyof ModelOptionsType;
};

// mapping of django content type models to MODEL_OPTIONS keys
export const CONTENT_TYPE_MODELS: ContentTypeModelsType = {
  artist: "artist",
  release: "release",
  track: "track",
  item: "item",
  transaction: "transaction",
  csvimport: "csvImport",
  period: "period",
  contract: "contract",
  payout: "payout",
  payee: "payee",
  ledger: "ledger",
  period_payee: "payeeSummary",
  split: "split",
  statement: "statement",
  statement_group: "statementGroup",
};

interface ModelBadgeProps {
  model?: keyof ModelOptionsType;
  className?: string;
  contentTypeModel?: keyof ContentTypeModelsType;
  body?: React.ReactNode | false;
}

const ModelBadge: React.FC<ModelBadgeProps> = ({
  model,
  className = "",
  contentTypeModel = null,
  body = false,
}) => {
  const _model = contentTypeModel
    ? CONTENT_TYPE_MODELS[contentTypeModel]
    : model;
  const options = _model
    ? MODEL_OPTIONS[_model as keyof ModelOptionsType]
    : undefined;
  return (
    <span
      className={`badge text-capitalize ${
        options ? options.className || "" : ""
      } ${className}`}
      style={options ? { backgroundColor: options.background } : {}}
    >
      {body ? body : options ? options.name : model}
    </span>
  );
};

export const ModelBadgeList: React.FC = () => {
  return (
    <div className="my-5 p-5 border">
      <h3>Model Badges</h3>
      <ModelBadge className="mb-2" model="artist" />
      <br />
      <ModelBadge className="mb-2" model="release" />
      <br />
      <ModelBadge className="mb-2" model="track" />
      <br />
      <ModelBadge className="mb-2" model="item" />
      <br />
      <ModelBadge className="mb-2" model="transaction" />
      <br />
      <ModelBadge className="mb-2" model="csvImport" />
      <br />
      <ModelBadge className="mb-2" model="period" />
      <br />
      <ModelBadge className="mb-2" model="contract" />
      <br />
      <ModelBadge className="mb-2" model="payee" />
      <br />
      <ModelBadge className="mb-2" model="ledger" />
      <br />
      <ModelBadge className="mb-2" model="payeeSummary" />
      <br />
      <ModelBadge className="mb-2" model="statement" />
      <br />
      <ModelBadge className="mb-2" model="payout" />
      <br />
    </div>
  );
};

export default ModelBadge;
